import React from 'react';
import { Box, ButtonV2, DialogV2, DialogV2Props, DrawerV2, DrawerV2Props, Flex, TextV2 } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { useRouterHelper } from '@shared/core';
import { Link } from '@react-router';
import { useDashboardTelemetry } from '../../../Dashboard.telemetry';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

interface Props
  extends Readonly<{
    isOpen: boolean;
    onClose: () => void;
  }> {}

const VIDEO_THUMBNAIL_URL = 'https://i.vimeocdn.com/video/1922603661-3f1d4e07910e4106362facc9d6e5bb80c85d7df87806ded7f39b3c835c39ed3c-d_1920x1080?r=pad';
const VIDEO_URL = 'https://player.vimeo.com/video/1005955590?h=304e2a0595&badge=0&autopause=0&player_id=0&app_id=58479&background=1';
const DRAWER_OVERRIDES: DrawerV2Props['overrides'] = {
  Content: {
    props: {
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4
    }
  },
  Body: {
    props: {
      paddingTop: [9, 9]
    }
  }
};

const DIALOG_OVERRIDES: DialogV2Props['overrides'] = {
  CloseButton: {
    props: {
      top: 4,
      right: 4
    }
  },
  Body: {
    props: {
      paddingY: [7, 7]
    }
  }
};

const Video = () => (
  <Box
    aspectRatio={'1920/1080'}
    backgroundImage={`url('${VIDEO_THUMBNAIL_URL}')`}
    backgroundSize="cover"
    backgroundPosition="center"
    flexShrink={0}
    overflow="hidden"
    width={'100%'}
    height={'100%'}
    position="relative"
  >
    <Box
      as="iframe"
      src={VIDEO_URL}
      allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
      position="absolute"
      top={0}
      left={0}
      width={'100%'}
      height={'100%'}
      border="none"
    />
  </Box>
);

const Content = () => {
  useDashboardTelemetry({ page: 'printDialog' });

  const { t } = useTranslation('adminDashboardV2');

  const trans = t('printEarlyAccessDialog');

  return (
    <Flex flexDirection="column" gap={6} alignItems="center">
      <Flex flexDirection={['column-reverse', null, 'column']} gap={[6, null, 5]} alignItems="center" width="100%" paddingBottom={[8, null, 0]}>
        <Flex flexDirection="column" textAlign="center" gap={2}>
          <TextV2 typographyVariant={['HedMobile32', null, 'Hed40']} fontFamily="Austin News Headline">
            {trans.eyebrow()}
          </TextV2>
          <TextV2 typographyVariant={['body1', null, 'body2']} maxWidth={460}>
            {trans.title()}
          </TextV2>
        </Flex>
        <Video />
      </Flex>

      <Flex display={['none', null, 'flex']} flexDirection="column" width="100%" gap={2}>
        <Flex width="100%" justifyContent="center">
          <TextV2 typographyVariant="body2">{trans.features[0]()}</TextV2>
          <TextV2 typographyVariant="body2" paddingX={4}>
            I
          </TextV2>
          <TextV2 typographyVariant="body2">{trans.features[1]()}</TextV2>
          <TextV2 typographyVariant="body2" paddingX={4}>
            I
          </TextV2>
          <TextV2 typographyVariant="body2">{trans.features[2]()}</TextV2>
        </Flex>
        <Flex width="100%" justifyContent="center">
          <TextV2 typographyVariant="body2">{trans.features[3]()}</TextV2>
          <TextV2 typographyVariant="body2" paddingX={4}>
            I
          </TextV2>
          <TextV2 typographyVariant="body2">{trans.features[4]()}</TextV2>
          <TextV2 typographyVariant="body2" paddingX={4}>
            I
          </TextV2>
          <TextV2 typographyVariant="body2">{trans.features[5]()}</TextV2>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const UpdatedContent: React.FC<Props> = props => {
  const { isOpen, onClose } = props;
  const isMobile = useIsMobileScreen();
  const routeHelpers = useRouterHelper();
  const { t } = useTranslation('adminDashboardV2');

  const trans = t('printEarlyAccessDialog');

  const { printEarlyAccessDialogCTAClicked, printEarlyAccessDialogDismissClicked } = useDashboardTelemetry();

  const handleCTAClick = () => {
    printEarlyAccessDialogCTAClicked();
  };

  const handleDialogDismiss = () => {
    printEarlyAccessDialogDismissClicked();
    onClose();
  };

  if (isMobile) {
    return (
      <DrawerV2 disableAutoFocus size="auto" overrides={DRAWER_OVERRIDES} isOpen={isOpen} onClose={handleDialogDismiss} anchor="bottom" useBackdrop>
        <DrawerV2.Body>
          <DrawerV2.CloseButton />
          <Content />
        </DrawerV2.Body>
        <DrawerV2.Footer borderTop="1px solid #EBEBEB">
          <ButtonV2 onClick={handleCTAClick} as={Link} to={routeHelpers.buildPath('/cards')} intent="neutral" shape="rounded" width="100%">
            {trans.cta()}
          </ButtonV2>
        </DrawerV2.Footer>
      </DrawerV2>
    );
  }

  return (
    <DialogV2 disableAutoFocus overrides={DIALOG_OVERRIDES} isOpen={isOpen} onClose={handleDialogDismiss} size="2xl">
      <DialogV2.CloseButton />
      <DialogV2.Body>
        <Content />
      </DialogV2.Body>
      <DialogV2.Footer borderTop="1px solid #EBEBEB" justifyContent="center">
        <ButtonV2 onClick={handleCTAClick} as={Link} to={routeHelpers.buildPath('/cards')} intent="neutral" shape="rounded" minWidth={340}>
          {trans.cta()}
        </ButtonV2>
      </DialogV2.Footer>
    </DialogV2>
  );
};
